/**
 * Anlass campaign executions and their channels (backend modules/Marketing,
 * CampaignExecutionController):
 * - GET  /anlass-campaign/{id}/executions — executions of a campaign;
 * - GET  /campaign-execution/{id} — one execution with channel history;
 * - POST /channel-execution/{id}/retry — retry a failed channel;
 * - GET  /anlass-campaign/{id}/kpi?from&to&conversion_days — KPI.
 */

/** One customer × one occasion. Summary of its channels. */
export enum CampaignExecutionStatusEnum {
  Created = "created",
  Ready = "ready",
  Processing = "processing",
  Completed = "completed",
  Partial = "partial",
  Skipped = "skipped",
  Failed = "failed",
}

/**
 * One channel of an execution. Accepted is a successful end for providers
 * that do not report delivery.
 */
export enum ChannelExecutionStatusEnum {
  Pending = "pending",
  Processing = "processing",
  Submitted = "submitted",
  Accepted = "accepted",
  Completed = "completed",
  FailedRetryable = "failed_retryable",
  FailedFinal = "failed_final",
  Skipped = "skipped",
  Cancelled = "cancelled",
}

export enum ExecutionSkipReasonEnum {
  Blacklist = "blacklist",
  NoDestination = "no_destination",
  NotEligible = "not_eligible",
  FrequencyCap = "frequency_cap",
  Duplicate = "duplicate",
  CampaignRule = "campaign_rule",
  Expired = "expired",
  NoEligibleChannel = "no_eligible_channel",
}

export enum MarketingChannelEnum {
  Email = "email",
  Sms = "sms",
  WhatsApp = "whatsapp",
  PrintMailing = "print_mailing",
  EPost = "epost",
}

export enum MarketingProviderEnum {
  BrevoTransactional = "brevo_transactional",
  Dinstar = "dinstar",
  Wazzup = "wazzup",
  EPost = "epost",
  Pma = "pma",
}

export enum ChannelExecutionEventTypeEnum {
  StatusChanged = "status_changed",
  ProviderEvent = "provider_event",
  Opened = "opened",
  Clicked = "clicked",
  ManualRetry = "manual_retry",
}

export interface ChannelExecutionEventResource {
  id: string;
  type: ChannelExecutionEventTypeEnum;
  from_status: ChannelExecutionStatusEnum | null;
  to_status: ChannelExecutionStatusEnum | null;
  provider_status: string | null;
  /** Identifiers and codes only — no personal data. */
  payload: Record<string, unknown> | null;
  occurred_at: string;
}

export interface ChannelExecutionResource {
  id: string;
  execution_id: string;
  channel: MarketingChannelEnum;
  provider: MarketingProviderEnum | null;
  owner_profile_id: string | null;
  status: ChannelExecutionStatusEnum;
  skip_reason: ExecutionSkipReasonEnum | null;
  planned_at: string;
  expires_at: string | null;
  attempt_count: number;
  next_retry_at: string | null;
  /** How to find the message at the provider (AC-09). */
  notification_message_id: string | null;
  provider_job_id: string | null;
  provider_status: string | null;
  correlation_id: string;
  submitted_at: string | null;
  completed_at: string | null;
  error_code: string | null;
  error_message: string | null;
  /** Only on GET /campaign-execution/{id}. */
  events?: ChannelExecutionEventResource[];
}

export interface CampaignExecutionResource {
  id: string;
  owner_id: string;
  source_type: string;
  source_id: string;
  campaign_version_id: string | null;
  customer_id: string;
  customer: { id: string; number: string | null; name: string | null } | null;
  trigger_type: string;
  occurrence_key: string;
  occurrence_date: string;
  is_test: boolean;
  test_run_id: string | null;
  scheduled_at: string;
  expires_at: string | null;
  status: CampaignExecutionStatusEnum;
  skip_reason: ExecutionSkipReasonEnum | null;
  error_code: string | null;
  error_message: string | null;
  /** Gutscheincode of the execution; null without coupon. */
  coupon_code: string | null;
  channels: ChannelExecutionResource[];
  created_at: string;
  updated_at: string;
}

/**
 * A metric is null when the provider does not supply it — not 0 (AC-15).
 */
export interface AnlassChannelKpi {
  planned: number;
  submitted: number;
  accepted: number;
  delivered: number | null;
  failed: number;
  skipped: number;
  opened: number | null;
  clicked: number | null;
}

export interface AnlassCampaignKpi {
  period: { from: string; to: string; conversion_days: number };
  /** Customers with an occasion in the period (test runs excluded). */
  recipients: number;
  /** …of them with at least one channel not skipped by the rules. */
  eligible: number;
  /** Customers greeted: a provider accepted at least one channel; confirmed — delivery reported. */
  customers: { greeted: number; confirmed: number };
  skipped_by_reason: Partial<Record<ExecutionSkipReasonEnum, number>>;
  channels: Partial<Record<MarketingChannelEnum, AnlassChannelKpi>>;
  /** null until redemption data from the shop exists (OPEN-INFO-03). */
  coupon_redeemed: number | null;
  conversion: {
    reached: number;
    converted: number;
    /** null without recipients — not 0 %. */
    rate: number | null;
    /** Window of the last recipient closes; until then the rate is preliminary. */
    final_at: string | null;
  };
}

/**
 * One row of the customer's communication history
 * (GET /customer/{id}/communications?owner_id&page&per_page):
 * a channel of an Anlass campaign execution or another Notification message.
 * No recipient addresses or texts.
 */
export interface CustomerCommunicationItem {
  id: string;
  kind: "campaign" | "notification";
  occurred_at: string | null;
  channel: string;
  status: string;
  /** Campaign name or notification type. */
  title: string | null;
  owner_id: string | null;
  campaign_id: string | null;
  execution_id: string | null;
  notification_message_id: string | null;
  skip_reason: ExecutionSkipReasonEnum | null;
  error_code: string | null;
  is_test: boolean;
  trigger_type: string | null;
}
